/**
 * 사용자 관리 API 클라이언트
 */

import axiosInstance from '../lib/axios';
import type {
  UserManagementResponseDto,
  UserManagementCreateRequestDto,
  UserManagementUpdateRequestDto,
  UserListParams,
  UserStatus,
  PageInfo,
  ApiResponse,
} from '../types/user.types';

/**
 * 사용자 관리 API 클라이언트
 */
export class UserApiClient {
  /**
   * 사용자 목록 조회 (페이징 및 필터)
   * @param params 필터 파라미터
   * @returns 페이징된 사용자 목록
   */
  static async getUsers(params: UserListParams): Promise<PageInfo<UserManagementResponseDto>> {
    try {
      const response = await axiosInstance.get<ApiResponse<PageInfo<UserManagementResponseDto>>>(
        '/management/users',
        { params }
      );
      return response.data.data;
    } catch (error) {
      console.error('사용자 목록 조회 실패:', error);
      throw new Error('사용자 목록을 불러오는 중 오류가 발생했습니다.');
    }
  }

  /**
   * 사용자 상세 조회
   * @param id 사용자 ID
   * @returns 사용자 상세 정보
   */
  static async getUserById(id: number): Promise<UserManagementResponseDto> {
    try {
      const response = await axiosInstance.get<ApiResponse<UserManagementResponseDto>>(
        `/management/users/${id}`
      );
      return response.data.data;
    } catch (error) {
      console.error('사용자 조회 실패:', error);
      throw new Error('사용자 정보를 불러오는 중 오류가 발생했습니다.');
    }
  }

  /**
   * 새 사용자 등록
   * @param request 사용자 생성 요청
   * @returns 생성된 사용자
   */
  static async createUser(
    request: UserManagementCreateRequestDto
  ): Promise<UserManagementResponseDto> {
    try {
      const response = await axiosInstance.post<ApiResponse<UserManagementResponseDto>>(
        '/management/users',
        request
      );
      return response.data.data;
    } catch (error) {
      console.error('사용자 등록 실패:', error);
      throw new Error('사용자 등록 중 오류가 발생했습니다.');
    }
  }

  /**
   * 사용자 정보 수정
   * @param id 사용자 ID
   * @param request 사용자 수정 요청
   * @returns 수정된 사용자
   */
  static async updateUser(
    id: number,
    request: UserManagementUpdateRequestDto
  ): Promise<UserManagementResponseDto> {
    try {
      const response = await axiosInstance.put<ApiResponse<UserManagementResponseDto>>(
        `/management/users/${id}`,
        request
      );
      return response.data.data;
    } catch (error) {
      console.error('사용자 수정 실패:', error);
      throw new Error('사용자 정보 수정 중 오류가 발생했습니다.');
    }
  }

  /**
   * 사용자 삭제
   * @param id 사용자 ID
   */
  static async deleteUser(id: number): Promise<void> {
    try {
      await axiosInstance.delete(`/management/users/${id}`);
    } catch (error) {
      console.error('사용자 삭제 실패:', error);
      throw new Error('사용자 삭제 중 오류가 발생했습니다.');
    }
  }

  /**
   * 사용자 상태 변경
   * @param id 사용자 ID
   * @param status 변경할 상태
   * @returns 수정된 사용자
   */
  static async updateUserStatus(
    id: number,
    status: UserStatus
  ): Promise<UserManagementResponseDto> {
    try {
      const response = await axiosInstance.patch<ApiResponse<UserManagementResponseDto>>(
        `/management/users/${id}/status`,
        null,
        {
          params: { status },
        }
      );
      return response.data.data;
    } catch (error) {
      console.error('사용자 상태 변경 실패:', error);
      throw new Error('사용자 상태 변경 중 오류가 발생했습니다.');
    }
  }

  /**
   * 비밀번호 변경
   * @param currentPassword 현재 비밀번호
   * @param newPassword 새 비밀번호
   * @returns 성공 여부
   */
  static async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    try {
      await axiosInstance.post('/auth/change-password', {
        currentPassword,
        newPassword,
      });
    } catch (error) {
      console.error('비밀번호 변경 실패:', error);
      throw error;
    }
  }

  /**
   * 비밀번호 만료 기간 연장 (6개월)
   * @returns 연장 결과 (성공 여부, 메시지, 새 만료까지 남은 일수)
   */
  static async extendPassword(): Promise<{
    success: boolean;
    message: string;
    daysUntilPasswordExpiry: number;
  }> {
    try {
      const response = await axiosInstance.post('/auth/extend-password');
      return response.data.data;
    } catch (error) {
      console.error('비밀번호 연장 실패:', error);
      throw error;
    }
  }

  /**
   * 아이디 중복 확인
   * @param username 확인할 아이디
   * @returns true: 사용 가능, false: 이미 사용 중
   */
  static async checkUsernameAvailability(username: string): Promise<boolean> {
    try {
      const response = await axiosInstance.get<ApiResponse<boolean>>(
        '/management/users/check-username',
        { params: { username } }
      );
      return response.data.data;
    } catch (error) {
      console.error('아이디 중복 확인 실패:', error);
      throw new Error('아이디 중복 확인 중 오류가 발생했습니다.');
    }
  }
}
